import { Button } from "components/Button/Button";
import { pendingImportsKey } from "components/Layout/Sidebar/components/CreateNew/CreateNew";
import { useExclusiveBlockingModalOpen } from "components/Modal/exclusiveBlockingModal";
import { centeredPopoverRootStyle } from "components/Popover/Popover";
import {
    NAVIGATION_PRIORITIES,
    useNavigationPrompt,
} from "components/Prompt/NavigationPromptContext";
import { AnyObject } from "final-form";
import { Location } from "history";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Prompt, useLocation } from "react-router-dom";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { ModalPrompt } from "./ModalPrompt";

interface BlockingNavigationModalProps {
    navigate: (path: { pathname: string; search: string }) => void;
    shouldBlockNavigation?: boolean;
    titleText: string;
    submitForm?: (e?: any) => Promise<any> | undefined;
    resetForm?: () => Promise<AnyObject>;
    isSubmitDisabled?: boolean;
    priority?: number;
    /**
     * Optional per-destination escape hatch: return true for a `nextLocation`
     * that should NOT count as "navigating away" (so the unsaved-changes prompt
     * is skipped and the navigation proceeds). Used to let same-record, in-page
     * navigations through — e.g. the tab-switch URL cleanup on the record screen,
     * which does a `history.replace` that would otherwise trip this guard even
     * though the record and its dirty form state are preserved.
     */
    allowNavigation?: (nextLocation: Location) => boolean;
}

export const BlockingNavigationModal = ({
    navigate,
    shouldBlockNavigation,
    titleText,
    submitForm,
    resetForm,
    isSubmitDisabled,
    priority = NAVIGATION_PRIORITIES.DEFAULT,
    allowNavigation,
}: BlockingNavigationModalProps) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { priority: currentPriority, setPriority } = useNavigationPrompt();
    // While an exclusive blocking modal (e.g. the stale-write "This record
    // changed" conflict dialog) owns the screen, suppress this leave-guard so the
    // two warning popups never stack. The conflict dialog is the more specific,
    // blocking data-safety decision and takes precedence; the leave-guard's local
    // state is preserved (only its popover is hidden) and reappears if still
    // relevant once the conflict dialog closes.
    const exclusiveModalOpen = useExclusiveBlockingModalOpen();
    const [isBlocking, setIsBlocking] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [lastLocation, setLastLocation] = useState<Location | null>(null);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);

    // Only block if this is the highest priority prompt
    const shouldShowPrompt = isBlocking && currentPriority === priority;

    useEffect(() => {
        if (shouldBlockNavigation) {
            if (priority >= currentPriority) {
                setPriority(priority);
                setIsBlocking(true);
            }
        } else {
            if (currentPriority === priority) {
                setPriority(0);
            }
            setIsBlocking(false);
        }

        return () => {
            if (currentPriority === priority) {
                setPriority(0);
            }
        };
    }, [shouldBlockNavigation, priority, currentPriority, setPriority]);

    const shouldBlockNavigationRef = useRef(isBlocking);

    useEffect(() => {
        shouldBlockNavigationRef.current = isBlocking;
    }, [isBlocking]);

    const onTabCloseOrRefresh = useCallback(
        (event) => {
            if (shouldBlockNavigationRef.current) {
                event.preventDefault();
                return (event.returnValue = titleText);
            }
        },
        [titleText]
    );

    useEffect(() => {
        window.addEventListener("beforeunload", onTabCloseOrRefresh);

        return () => {
            window.removeEventListener("beforeunload", onTabCloseOrRefresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const closeModal = useCallback(() => {
        setModalVisible(false);
        setConfirmedNavigation(false);
        // clean pending imports
        dispatch({ type: dataStoreAction.CLEAN_DATA, key: pendingImportsKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBlockedNavigation = useCallback(
        (nextLocation: Location): boolean => {
            // Same-record / in-page navigations (e.g. the tab-switch URL cleanup)
            // are not "leaving" — let them through without prompting.
            if (allowNavigation?.(nextLocation)) {
                return true;
            }
            if (!confirmedNavigation && shouldShowPrompt) {
                setModalVisible(true);
                setLastLocation(nextLocation);
                return false;
            }
            return true;
        },
        [allowNavigation, confirmedNavigation, shouldShowPrompt]
    );

    const handleConfirmNavigationClick = useCallback(() => {
        setModalVisible(false);
        setConfirmedNavigation(true);
    }, []);

    const onConfirm = useCallback(
        async () =>
            resetForm
                ? await resetForm().then(handleConfirmNavigationClick)
                : handleConfirmNavigationClick(),
        [handleConfirmNavigationClick, resetForm]
    );

    const saveAndConfirmNavigation = useCallback(async () => {
        if (submitForm) {
            try {
                await submitForm();
                setConfirmedNavigation(true);
            } catch (error) {
                setConfirmedNavigation(false);
            } finally {
                setModalVisible(false);
            }
        }
    }, [submitForm]);

    useEffect(() => {
        if (confirmedNavigation && lastLocation) {
            navigate({ pathname: lastLocation.pathname, search: lastLocation.search || "" });
            setConfirmedNavigation(false);
        }
    }, [confirmedNavigation, lastLocation, navigate]);

    return (
        <>
            <Prompt when={shouldShowPrompt} message={handleBlockedNavigation} />
            <ModalPrompt
                isOpen={modalVisible && !exclusiveModalOpen}
                titleText={titleText}
                confirmButtonText="Don't save"
                confirmButtonVariation="danger"
                cancelButtonText="Go back"
                cancelButtonVariation="white"
                onCancel={closeModal}
                onConfirm={onConfirm}
                additionalActionButton={
                    submitForm && (
                        <Button
                            variation="primary"
                            onClick={saveAndConfirmNavigation}
                            label="Save"
                            disabled={isSubmitDisabled}
                        />
                    )
                }
                rootStyle={{ ...centeredPopoverRootStyle, zIndex: 20000 }}
            />
        </>
    );
};
